import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Tag } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import { OptionSignal } from '@/components';
import { getRiskColorForTag, getSignalTranslation } from '@/helpers';
import styles from './EntitySelect.module.sass';

const popupStyles = { popup: { root: { zIndex: 10000 } } } as const;

const DEFAULT_MAX_TAG_COUNT = 5;

export type EntitySelectOption = {
  label: string;
  value: string;
};

type EntitySelectProps = {
  value?: string | string[];
  options: EntitySelectOption[];
  placeholder?: string;
  allowClear?: boolean;
  showSearch?: boolean;
  className?: string;
  status?: 'error' | 'warning';
  isMultiple?: boolean;
  maxTagCount?: number | 'responsive';
  notFoundContent?: React.ReactNode;
  popupMatchSelectWidth?: boolean;
  onChange?: (value: string | string[] | undefined) => void;
};

const renderOptionSignal = (option: BaseOptionType) => (
  <OptionSignal value={option.value} />
);

export const EntitySelect = ({
  options,
  value,
  onChange,
  placeholder,
  allowClear = true,
  showSearch = true,
  className,
  status,
  isMultiple = false,
  maxTagCount = DEFAULT_MAX_TAG_COUNT,
  notFoundContent,
  popupMatchSelectWidth = false,
}: EntitySelectProps) => {
  const { t } = useTranslation();

  const selectValue = isMultiple
    ? (value as string[] | undefined) ?? []
    : (value as string | undefined) || undefined;

  const effectiveMaxTagCount = isMultiple ? maxTagCount : undefined;

  const allValues = options.map((o) => o.value);

  const dropdownRender = useCallback(
    (menu: React.ReactNode) => (
      <>
        {menu}
        {isMultiple && options.length > 0 && (
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.selectAllBtn}
              onClick={() => onChange?.(allValues)}
            >
              {t('translation.select_all')}
            </button>
            <button
              type="button"
              className={styles.clearAllBtn}
              onClick={() => onChange?.([])}
            >
              {t('translation.clear_all')}
            </button>
          </div>
        )}
      </>
    ),
    [isMultiple, options, allValues, onChange, t],
  );

  const tagRender = useCallback(
    ({
      value: tagValue,
      label,
      closable,
      onClose,
    }: {
      value: string;
      label: React.ReactNode;
      closable: boolean;
      onClose: () => void;
    }) => {
      const isOverflowPlaceholder =
        typeof label === 'string' && label.startsWith('+');
      if (isOverflowPlaceholder) {
        return <span className={styles.tagPlaceholder}>{label as string}</span>;
      }
      return (
        <Tag
          closable={closable}
          className={styles.tag}
          color={getRiskColorForTag(tagValue)}
          onClose={onClose}
        >
          {getSignalTranslation(tagValue)}
        </Tag>
      );
    },
    [],
  );

  return (
    <Select
      mode={isMultiple ? 'multiple' : undefined}
      value={selectValue}
      size="large"
      options={options}
      styles={popupStyles}
      placeholder={placeholder}
      allowClear={allowClear}
      showSearch={showSearch}
      status={status}
      maxTagCount={effectiveMaxTagCount}
      maxTagPlaceholder={
        effectiveMaxTagCount
          ? (omittedValues) => `+${omittedValues?.length ?? 0}`
          : undefined
      }
      notFoundContent={notFoundContent}
      popupMatchSelectWidth={popupMatchSelectWidth}
      optionRender={renderOptionSignal}
      tagRender={tagRender}
      filterOption={filterOptions}
      optionLabelProp="label"
      dropdownRender={isMultiple ? dropdownRender : undefined}
      className={className ?? styles.selectWhite}
      onChange={onChange}
    />
  );
};

const filterOptions = (input: string, option?: EntitySelectOption) => {
  if (!option) return false;
  return option.label.toLowerCase().includes(input.toLowerCase());
};
